import ChevronLeftIcon from '@ordr-as/icons/ChevronLeftIcon';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { Dispatch, FC, SetStateAction, useCallback } from 'react';
import { MenuItemVariantDto } from '../../../../../../../generated-api';
import { CartAdditionOption, CartOrderLine } from '../../../../../../../types/cart';
import { useScreenSize } from '../../../../../../context/ScreenSizeContext';
import { isRequiredHandled } from '../../../../../../utils/customizations';
import OptionGroupSection from '../../../../items/customization/OptionGroupSection';
import ComponentListRow from './composite/ComponentListRow';
import LeafComponentSection from './composite/LeafComponentSection';
import { ExpandedComponent } from './composite/types';
import { useCompositeEditState } from './composite/useCompositeEditState';
import { useCompositeNavigation } from './composite/useCompositeNavigation';

interface Props {
  onModalClose: VoidFunction;
  parentOrderLine: CartOrderLine;
  parentItem: MenuItemVariantDto;
}

/** Build a subtitle summarizing the component's selected options */
const getComponentSubtitle = (
  component: ExpandedComponent,
  optionsMap: Map<string, CartAdditionOption[]>
): string | undefined => {
  const options = optionsMap.get(component.instanceId);
  if (!options || options.length === 0) {
    return undefined;
  }
  return options.map((o) => o.option.name).join(', ');
};

const EditCompositeItemModal: FC<Props> = ({ parentOrderLine, parentItem, onModalClose }) => {
  const { isPhone } = useScreenSize();

  const editState = useCompositeEditState(parentOrderLine, parentItem);

  const { push, pop, currentComponent, currentTitle, viewMode, listComponents, canGoBack } = useCompositeNavigation(
    editState.expandedRootComponents,
    { skipSingleItemRoot: true }
  );

  const handleAction = useCallback(() => {
    editState.handleSave();
    if (editState.isValid) {
      onModalClose();
    }
  }, [editState, onModalClose]);

  const dialogTitle = currentTitle ?? parentItem.name;

  return (
    <Dialog
      onClose={onModalClose}
      title={dialogTitle}
      actionLabel={editState.actionLabel}
      onAction={handleAction}
      isDisabledAction={!editState.isValid}
      size={isPhone ? 'fullwidth' : 'default'}
      TitleIcon={canGoBack ? ChevronLeftIcon : undefined}
      titleIconAction={canGoBack ? pop : undefined}
      titleIconClassNames={canGoBack ? '!mr-1 !p-0' : undefined}
    >
      <article className="flex flex-col gap-3">
        {viewMode === 'root' && (
          <RootContent
            parentItem={parentItem}
            parentOptions={editState.parentOptions}
            setParentOptions={editState.setParentOptions}
          />
        )}

        {viewMode === 'component-detail' && currentComponent && (
          <ComponentDetail
            instanceId={currentComponent.instanceId}
            itemVariantId={currentComponent.itemVariantId}
            editState={editState}
          />
        )}

        {(viewMode === 'root' || viewMode === 'component-list') &&
          listComponents.map((component) => (
            <ComponentListRow
              key={component.instanceId}
              component={component}
              subtitle={getComponentSubtitle(component, editState.componentOptionsMap)}
              isValid={editState.isSubtreeValid(component)}
              onClick={() =>
                push({
                  instanceId: component.instanceId,
                  itemVariantId: component.itemVariantId,
                  name: component.name,
                })
              }
            />
          ))}
      </article>
    </Dialog>
  );
};

interface RootContentProps {
  parentItem: MenuItemVariantDto;
  parentOptions: CartAdditionOption[];
  setParentOptions: Dispatch<SetStateAction<CartAdditionOption[]>>;
}

const RootContent: FC<RootContentProps> = ({ parentItem, parentOptions, setParentOptions }) => (
  <>
    {parentItem.optionGroups.length > 0 && (
      <div className="flex flex-col gap-2">
        {parentItem.description && <p className="text-small">{parentItem.description}</p>}
        {parentItem.allergens.length > 0 && (
          <p className="text-grey-dark text-small">{parentItem.allergens.map((a) => a.name).join(', ')}</p>
        )}
        {parentItem.optionGroups.map((optionGroup) => (
          <OptionGroupSection
            key={optionGroup.variantLinkId}
            optionGroup={optionGroup}
            setOptions={setParentOptions}
            defaultOptions={parentOptions}
            isRequiredHandled={isRequiredHandled(optionGroup, parentOptions)}
          />
        ))}
      </div>
    )}
  </>
);

interface ComponentDetailProps {
  instanceId: string;
  itemVariantId: string;
  editState: ReturnType<typeof useCompositeEditState>;
}

const ComponentDetail: FC<ComponentDetailProps> = ({ instanceId, itemVariantId, editState }) => {
  const item = editState.getMenuItemForComponent(itemVariantId);
  const { updateComponentOptions } = editState;

  const onOptionsChange = useCallback(
    (optionsOrFn: CartAdditionOption[] | ((prev: CartAdditionOption[]) => CartAdditionOption[])) => {
      updateComponentOptions(instanceId, optionsOrFn);
    },
    [updateComponentOptions, instanceId]
  );

  if (!item) {
    return null;
  }

  return (
    <LeafComponentSection
      item={item}
      options={editState.componentOptionsMap.get(instanceId) || []}
      onOptionsChange={onOptionsChange}
    />
  );
};

export default EditCompositeItemModal;
